import Footer from './footer';
import Services from './landing-services';
import MarketRadarBanner from './market-radar-banner';
import WealthAssistantBanner from './wealth-assistant-banner';
import FreeCalculators from './free-calculators';
import RunningLineAbout from './running-line-about';

const DEPTH_LAYERS = 12;
const DEPTH_STEP_PX = 4;
const FRONT_Z_PX = (DEPTH_LAYERS * DEPTH_STEP_PX) / 2;
const BACK_Z_PX = -FRONT_Z_PX - 2;

const WatermarkWordmark = ({ solid }: { solid: boolean }) => (
    <>
        <div
            className={`text-[15vw] sm:text-[130px] lg:text-[170px] font-black leading-none tracking-tighter ${
                solid ? 'text-slate-900 dark:text-white' : 'text-emerald-900 dark:text-emerald-200'
            }`}
        >
            FORGE
        </div>
        <div
            className={`text-[8.5vw] sm:text-[74px] lg:text-[96px] font-black leading-none tracking-widest mt-2 sm:mt-4 ${
                solid
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-300'
                    : 'text-emerald-700 dark:text-emerald-400'
            }`}
        >
            ULTRA
        </div>
    </>
);

/**
 * Oversized 3D/4D extruded Forge Ultra watermark. `perspective` + `opacity` live on
 * the outer (non-3D) wrapper so the preserve-3d subtree below is never flattened.
 */
const Watermark3D = ({ className, reverse = false }: { className: string; reverse?: boolean }) => (
    <div className={`absolute opacity-[0.09] dark:opacity-[0.16] fu-perspective ${className}`}>
        <div className={`relative w-full h-full fu-preserve-3d ${reverse ? 'fu-reverse' : ''}`}>
            {/* 4D Multi-Axis Gyroscope Rings (X, Y, Z) */}
            <div className="absolute inset-[4%] rounded-full border-2 border-slate-900/70 dark:border-white/70 fu-ring-x"></div>
            <div className="absolute inset-[11%] rounded-full border-2 border-dashed border-emerald-500/80 fu-ring-y"></div>
            <div className="absolute inset-[18%] rounded-full border border-green-500/70 fu-ring-z"></div>

            {/* Extruded 3D Logo — continuous 360° revolution on a fixed tilted plane */}
            <div className="absolute inset-0 flex items-center justify-center fu-preserve-3d select-none fu-logo-spin">
                {/* Z-axis extrusion layers (physical thickness) */}
                {Array.from({ length: DEPTH_LAYERS }, (_, i) => (
                    <div
                        key={`depth-${i}`}
                        className="absolute inset-0 flex flex-col items-center justify-center"
                        style={{ transform: `translateZ(${FRONT_Z_PX - (i + 1) * DEPTH_STEP_PX}px)`, opacity: 0.35 - i * 0.015 }}
                    >
                        <WatermarkWordmark solid={false} />
                    </div>
                ))}

                {/* Back face — mirrored so it reads correctly when the logo turns away */}
                <div
                    className="absolute inset-0 flex flex-col items-center justify-center fu-face"
                    style={{ transform: `translateZ(${BACK_Z_PX}px) rotateY(180deg)` }}
                >
                    <WatermarkWordmark solid />
                </div>

                {/* Front face */}
                <div
                    className="absolute inset-0 flex flex-col items-center justify-center fu-face"
                    style={{ transform: `translateZ(${FRONT_Z_PX}px)` }}
                >
                    <WatermarkWordmark solid />
                </div>
            </div>
        </div>
    </div>
);

const LandingPage = () => {
    const handleLogin = () => { window.location.href = 'https://www.ki-wealth.com/login'; };
    const handleRegister = () => { window.location.href = 'https://ki-wealth.com/membership-levels'; };

    return (
        <div className="relative isolate min-h-screen flex flex-col">
            {/* Custom Styles for the 4D Gyroscope & 3D Extruded Watermark */}
            <style>{`
                .fu-perspective { perspective: 1500px; }
                .fu-preserve-3d { transform-style: preserve-3d; }
                .fu-face { backface-visibility: hidden; -webkit-backface-visibility: hidden; }
                @keyframes fu-ring-x {
                    from { transform: rotateY(20deg) rotateX(0deg); }
                    to   { transform: rotateY(20deg) rotateX(360deg); }
                }
                @keyframes fu-ring-y {
                    from { transform: rotateX(20deg) rotateY(0deg); }
                    to   { transform: rotateX(20deg) rotateY(360deg); }
                }
                @keyframes fu-ring-z {
                    from { transform: rotateX(65deg) rotateZ(0deg); }
                    to   { transform: rotateX(65deg) rotateZ(360deg); }
                }
                @keyframes fu-logo-spin {
                    from { transform: rotateX(12deg) rotateZ(-4deg) rotateY(0deg); }
                    to   { transform: rotateX(12deg) rotateZ(-4deg) rotateY(360deg); }
                }
                .fu-ring-x { animation: fu-ring-x 40s linear infinite; }
                .fu-ring-y { animation: fu-ring-y 45s linear infinite; }
                .fu-ring-z { animation: fu-ring-z 50s linear infinite; }
                .fu-logo-spin { animation: fu-logo-spin 28s linear infinite; will-change: transform; }
                .fu-reverse .fu-ring-x, .fu-reverse .fu-ring-y, .fu-reverse .fu-ring-z, .fu-reverse .fu-logo-spin { animation-direction: reverse; }
                @media (prefers-reduced-motion: reduce) {
                    .fu-ring-x, .fu-ring-y, .fu-ring-z, .fu-logo-spin { animation: none; }
                }
            `}</style>

            {/* Background Glow Effect, Tech Grid & 3D Watermark — confined to its own
                overflow-hidden wrapper so it never clips foreground content. */}
            <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none" aria-hidden="true">
                {/* High-Tech Blueprint Grid */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808014_1px,transparent_1px),linear-gradient(to_bottom,#80808014_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,#000_70%,transparent_100%)]"></div>

                {/* Original Glows */}
                <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[50%] bg-green-900/100 rounded-full blur-[120px]"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[40%] bg-indigo-700/50 rounded-full blur-[100px]"></div>

                {/* Top-Right 3D/4D Watermark — intentionally oversized & clipped off-screen */}
                <Watermark3D className="-top-[14vw] -right-[45vw] w-[115vw] h-[115vw] sm:-top-[220px] sm:-right-[300px] sm:w-[820px] sm:h-[820px] lg:-top-[260px] lg:-right-[280px] lg:w-[1050px] lg:h-[1050px]" />

                {/* Left 3D/4D Watermark — mirrored counterpart, counter-rotating & offset lower
                    for an asymmetric composition (hidden on small screens to avoid overlap) */}
                <Watermark3D
                    reverse
                    className="hidden md:block md:top-[120px] md:-left-[380px] md:w-[760px] md:h-[760px] lg:top-[80px] lg:-left-[400px] lg:w-[960px] lg:h-[960px]"
                />

                {/* Readability scrim — softly lifts contrast behind the hero copy */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[min(1100px,100%)] h-[90vh] bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.55),transparent_65%)] dark:bg-[radial-gradient(ellipse_at_center,rgba(2,6,23,0.55),transparent_65%)]"></div>
            </div>

            {/* Hero */}
            <header className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 md:pt-32 pb-12 sm:pb-16">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mb-5 sm:mb-6">
                        <span className="px-3 py-1 text-[10px] sm:text-xs font-bold tracking-widest text-green-700 dark:text-green-400 uppercase bg-green-500/10 dark:bg-green-400/10 rounded-full border border-green-600/30 dark:border-green-400/25">
                            Now Live
                        </span>
                        <div className="hidden sm:block h-px w-8 md:w-12 bg-slate-300 dark:bg-slate-700"></div>
                        <span className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-300">
                            The Future of Wealth
                        </span>
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tight text-slate-900 dark:text-white mb-6 sm:mb-8 break-words">
                        Forge <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-500 dark:from-green-400 dark:to-emerald-300">Ultra</span>
                    </h1>

                    <p className="text-base sm:text-xl md:text-2xl text-slate-700 dark:text-slate-200 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed">
                        More than an investing platform. Forge Ultra&trade; helps you understand the
                        decisions that shape long-term wealth through advanced analytics, practical
                        education, and powerful planning tools.
                    </p>

                    <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-center gap-3 sm:gap-4">
                        {/* Login — solid, high-contrast */}
                        <button
                            type="button"
                            onClick={handleLogin}
                            className="group relative inline-flex h-[52px] sm:h-[56px] w-full sm:w-auto min-w-0 sm:min-w-[168px]
                                       items-center justify-center gap-2.5 px-7 sm:px-8
                                       rounded-2xl text-[15px] sm:text-base font-semibold tracking-tight
                                       bg-slate-900 text-white dark:bg-white dark:text-slate-900
                                       shadow-lg shadow-slate-900/20 dark:shadow-black/30
                                       ring-1 ring-inset ring-white/10 dark:ring-black/5
                                       hover:-translate-y-0.5 hover:shadow-xl hover:shadow-slate-900/30
                                       active:translate-y-0
                                       transition-all duration-300
                                       focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                        >
                            <i className="bx bx-log-in-circle text-xl transition-transform duration-300 group-hover:-translate-x-0.5" aria-hidden="true"></i>
                            Login
                        </button>

                        {/* Create Account — refined glass / outline */}
                        <button
                            type="button"
                            onClick={handleRegister}
                            className="group relative inline-flex h-[52px] sm:h-[56px] w-full sm:w-auto min-w-0 sm:min-w-[168px]
                                       items-center justify-center gap-2.5 px-7 sm:px-8
                                       rounded-2xl text-[15px] sm:text-base font-semibold tracking-tight
                                       bg-white/60 dark:bg-white/5 backdrop-blur-md
                                       text-slate-900 dark:text-white
                                       border border-slate-300/80 dark:border-white/15
                                       shadow-sm
                                       hover:-translate-y-0.5 hover:border-emerald-400/70 hover:shadow-lg hover:shadow-emerald-500/10
                                       hover:bg-white/80 dark:hover:bg-white/10
                                       active:translate-y-0
                                       transition-all duration-300
                                       focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                        >
                            <i className="bx bx-user-plus text-xl transition-transform duration-300 group-hover:scale-110" aria-hidden="true"></i>
                            Create Account
                        </button>

                        {/* Discover Your Future Wealth — primary gradient CTA with shine sweep */}
                        <a
                            href="#free-calculators"
                            className="group relative inline-flex h-[52px] sm:h-[56px] w-full sm:w-auto
                                       items-center justify-center gap-2.5 px-7 sm:px-9
                                       overflow-hidden rounded-2xl text-[15px] sm:text-base font-bold tracking-tight
                                       bg-gradient-to-r from-green-600 via-emerald-600 to-emerald-500 bg-[length:200%_100%] bg-left
                                       text-white
                                       shadow-lg shadow-emerald-500/30
                                       hover:-translate-y-0.5 hover:bg-right hover:shadow-2xl hover:shadow-emerald-500/50
                                       active:translate-y-0
                                       transition-all duration-500
                                       focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                        >
                            {/* moving shine highlight */}
                            <span
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 -translate-x-full
                                           bg-gradient-to-r from-transparent via-white/35 to-transparent
                                           transition-transform duration-700 ease-out
                                           group-hover:translate-x-full"
                            />
                            <i className="bx bx-calculator text-xl" aria-hidden="true"></i>
                            <span className="relative">Discover Your Future Wealth</span>
                            <i className="bx bx-right-arrow-alt text-xl transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true"></i>
                        </a>
                    </div>

                    {/* Professional High-Tech Social Proof Section */}
                    <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                        <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-white/80 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 shadow-sm backdrop-blur-sm">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 motion-reduce:animate-none"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                            </span>
                            <span className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 uppercase tracking-widest">
                                Platform Active
                            </span>
                        </div>
                        <a
                            href="https://www.ki-wealth.com/membership-levels"
                            className="text-slate-600 dark:text-slate-300 text-sm font-medium hover:text-emerald-600 dark:hover:text-emerald-400 underline-offset-4 hover:underline transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 rounded"
                        >
                            Get Exclusive Access to Forge Ultra Advanced Wealth Intelligence Platform
                        </a>
                    </div>
                </div>
            </header>

            {/* Content sections */}
            <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-32 space-y-20 sm:space-y-28">
                <Services />
                <MarketRadarBanner />
                <WealthAssistantBanner />
                <FreeCalculators />
            </main>

            <RunningLineAbout />
            <Footer />
        </div>
    );
};

export default LandingPage;